import { IStairCalculationResult, IStairConfig } from '@stair-craft/common/util';

import { horizontalDim, verticalDim } from './dim-builders';
import { pushElevationRunLengths, pushElevationTreadMarks } from './elevation-marks';
import { IElevationMap, pushElevationRailings, pushElevationSupports, stairRailingTop } from './elevation-railing';
import {
    EColorRef,
    EPrimitiveKind,
    IPaneRect,
    IPlanPrimitive,
    IPoint,
    IProjectionOutput,
    IViewFit,
    emptyProjection,
} from './plan-primitive';
import { ITextMeasurer } from './text-measurer';

/** Side overhang of the treads past the stair width in the front view — its origin sits this far left of the stair. */
export const FRONT_VIEW_OVERHANG_MM: number = 50;
const PAD_LEFT: number = 50;
const PAD_RIGHT: number = 40;
const PAD_TOP: number = 28;
const PAD_BOTTOM: number = 36;
const DIM_LINE_ABOVE: number = 14;
const DIM_LINE_LEFT: number = 16;

function rectPoly(x: number, y: number, w: number, h: number, fill: EColorRef, stroke: EColorRef, strokeWidth: number): IPlanPrimitive {
    const points: IPoint[] = [
        [x, y],
        [x + w, y],
        [x + w, y + h],
        [x, y + h],
    ];
    return { kind: EPrimitiveKind.Poly, closed: true, width: strokeWidth, points, fill, stroke };
}

/**
 * Front view (вид спереди) — straight stair silhouette: outer envelope, inner
 * shaft, per-step horizontal lines, and width/overhang/rise dimensions.
 * Pure projection in pane-local pixels; the drawer translates to the pane origin.
 * 1:1 port of the former PlanViewComponent#drawFrontView. With `fit` the sheet sets the
 * scale and the pane point of the millimetre origin (the silhouette's left edge at the floor).
 */
export function projectFrontView(
    result: IStairCalculationResult,
    config: IStairConfig,
    pane: IPaneRect,
    measurer: ITextMeasurer,
    fit?: IViewFit
): IProjectionOutput {
    const stepCount: number = result.stepCount;
    const totalRise: number = result.room.floorHeight;
    const stairWidth: number = config.stairWidth;

    if (stepCount <= 0 || totalRise <= 0 || stairWidth <= 0) {
        return emptyProjection();
    }

    const innerW: number = pane.w - PAD_LEFT - PAD_RIGHT;
    const innerH: number = pane.h - PAD_TOP - PAD_BOTTOM;

    const worldW: number = stairWidth + FRONT_VIEW_OVERHANG_MM * 2;
    // The railing of the last treads rises over the upper floor line: the frame holds it too.
    const railTop: number = stairRailingTop(result, config);
    const worldH: number = Math.max(totalRise, railTop);
    const scale: number = fit?.pxPerMm ?? Math.min(innerW / worldW, innerH / worldH);
    const drawW: number = worldW * scale;
    const drawH: number = totalRise * scale;
    const ox: number = fit ? fit.origin.x : PAD_LEFT + (innerW - drawW) / 2;
    const floorY: number = fit ? fit.origin.y : PAD_TOP + (innerH + worldH * scale) / 2;
    const oy: number = floorY - drawH;
    // The dimensions above stand over the railing as well as over the silhouette.
    const dimY: number = Math.min(oy, floorY - railTop * scale) - DIM_LINE_ABOVE;

    const innerOx: number = ox + FRONT_VIEW_OVERHANG_MM * scale;
    const innerShaftW: number = stairWidth * scale;

    const primitives: IPlanPrimitive[] = [];

    // Outer envelope (full silhouette).
    primitives.push(rectPoly(ox, oy, drawW, drawH, EColorRef.Paper2, EColorRef.Ink2, 1.4));
    // Inner stair shaft (without overhang).
    primitives.push(rectPoly(innerOx, oy, innerShaftW, drawH, EColorRef.Step, EColorRef.StepStroke, 1));

    // Horizontal step lines (top of each step).
    const stepH: number = drawH / stepCount;
    for (let i: number = 1; i < stepCount; i++) {
        const y: number = oy + drawH - i * stepH;
        primitives.push({
            kind: EPrimitiveKind.Poly,
            points: [
                [ox, y],
                [ox + drawW, y],
            ],
            closed: false,
            stroke: EColorRef.StepStroke,
            width: 1,
        });
    }

    // Railing — from the calculation's railing geometry, as on every elevation. The view's
    // millimetre origin is the silhouette's left edge: the first tread's X less the overhang.
    const leftX: number = (result.geometry.steps.at(0)?.position.x ?? 0) - FRONT_VIEW_OVERHANG_MM;
    const elevation: IElevationMap = {
        across: (point: { x: number; z: number }): number => point.x - leftX,
        wx: (mm: number): number => ox + mm * scale,
        wy: (mm: number): number => floorY - mm * scale,
        stairOnly: true,
    };
    pushElevationSupports(primitives, result, elevation);
    pushElevationRailings(primitives, result, config, elevation);
    pushElevationTreadMarks(primitives, result, elevation, measurer);
    pushElevationRunLengths(primitives, result, config, elevation);

    // Dimensions: shaft width, tread overhang, total rise.
    primitives.push(horizontalDim(innerOx, dimY, innerOx + innerShaftW, String(stairWidth), measurer));
    primitives.push(horizontalDim(ox + drawW - FRONT_VIEW_OVERHANG_MM * scale, dimY, ox + drawW, String(FRONT_VIEW_OVERHANG_MM), measurer));
    primitives.push(verticalDim(ox - DIM_LINE_LEFT, oy, oy + drawH, String(Math.round(totalRise)), measurer));

    // Millimetre origin: the silhouette's left edge at floor level (world x = 0, y = 0).
    return { primitives, pxPerMm: scale, origin: { x: ox, y: oy + drawH } };
}
